import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useConversation } from '@11labs/react';
import { useLanguage } from './LanguageContext';

export type CallStatus =
  | 'idle'
  | 'requesting_mic'
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'error';

export type CallError =
  | 'mic_denied'
  | 'mic_unavailable'
  | 'session_failed'
  | 'connection_lost'
  | null;

export interface TranscriptEntry {
  role: 'user' | 'agent';
  text: string;
  timestamp: number;
}

interface VoiceCallContextValue {
  status: CallStatus;
  error: CallError;
  transcript: TranscriptEntry[];
  isSpeaking: boolean;
  startCall: () => Promise<void>;
  endCall: () => Promise<void>;
  dismissHUD: () => void;
}

const VoiceCallContext = createContext<VoiceCallContextValue | null>(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const track = (event: string, props?: Record<string, unknown>) => {
  console.debug('[voice-call]', event, props);
};

// ─────────────────────────────────────────────────────────────────────────────
// ConversationManager
//
// Isolated via React.memo so that VoiceCallProvider state changes (transcript,
// status, isSpeaking) never cause useConversation to be re-invoked.
// Without this separation every setTranscript() triggers a re-render of the
// provider, which re-runs useConversation's internal effects and the SDK tears
// down the WebSocket immediately after a message arrives.
// ─────────────────────────────────────────────────────────────────────────────
interface ConversationManagerProps {
  onConnect: () => void;
  onDisconnect: () => void;
  onMessage: (p: { message: string; source: string }) => void;
  onError: (m: string) => void;
  onIsSpeakingChange: (speaking: boolean) => void;
  // typed loosely — we only call startSession / endSession on it
  conversationRef: React.MutableRefObject<any>;
}

const ConversationManager = React.memo<ConversationManagerProps>(({
  onConnect, onDisconnect, onMessage, onError, onIsSpeakingChange, conversationRef,
}) => {
  const conversation = useConversation({ onConnect, onDisconnect, onMessage, onError });

  // Keep ref in sync so startCall / endCall always use the latest handle
  conversationRef.current = conversation;

  // Propagate isSpeaking state upward without causing ConversationManager to
  // re-render from the parent state update (React.memo blocks that path)
  useEffect(() => {
    onIsSpeakingChange(conversation.isSpeaking);
  }, [conversation.isSpeaking, onIsSpeakingChange]);

  // Page-unload cleanup lives here because this is the stable component that
  // holds the real conversation handle
  useEffect(() => {
    const handleUnload = () => { conversationRef.current?.endSession().catch(() => {}); };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
});
ConversationManager.displayName = 'ConversationManager';

// ─────────────────────────────────────────────────────────────────────────────
// VoiceCallProvider
// ─────────────────────────────────────────────────────────────────────────────
export const VoiceCallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<CallStatus>('idle');
  const [error, setError] = useState<CallError>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const conversationRef = useRef<any>(null);
  const { language } = useLanguage();
  const languageRef = useRef(language);
  languageRef.current = language; // always current without needing it in deps

  // All callbacks use empty deps — React guarantees setState functions are stable
  const onConnect = useCallback(() => {
    setStatus('connected');
    track('voice_call_session_started');
  }, []);

  const onDisconnect = useCallback(() => {
    setStatus('ended');
    track('voice_call_ended');
    setTimeout(() => setStatus('idle'), 3000);
  }, []);

  const onMessage = useCallback(({ message, source }: { message: string; source: string }) => {
    setTranscript(prev => [
      ...prev,
      { role: source === 'ai' ? 'agent' : 'user', text: message, timestamp: Date.now() },
    ]);
    track('voice_call_message', { role: source, textLength: message.length });
  }, []);

  const onError = useCallback((message: string) => {
    setError('connection_lost');
    setStatus('error');
    track('voice_call_error', { errorType: 'connection_lost', message });
  }, []);

  const onIsSpeakingChange = useCallback((speaking: boolean) => {
    setIsSpeaking(speaking);
  }, []);

  const startCall = useCallback(async () => {
    setError(null);
    setTranscript([]);
    setStatus('requesting_mic');
    track('voice_call_initiated');

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      track('voice_call_mic_granted');
    } catch (e: any) {
      setError(e.name === 'NotAllowedError' ? 'mic_denied' : 'mic_unavailable');
      setStatus('error');
      track('voice_call_mic_denied');
      return;
    }

    setStatus('connecting');

    try {
      const response = await fetch(`${API_BASE_URL}/voice-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({}),
      });

      if (!response.ok) throw new Error(`${response.status}`);

      const data = await response.json();

      const langOverride = { overrides: { agent: { language: languageRef.current } } };

      if (data.sessionToken) {
        await conversationRef.current?.startSession({ signedUrl: data.sessionToken, ...langOverride });
      } else if (data.agentId) {
        await conversationRef.current?.startSession({ agentId: data.agentId, ...langOverride });
      } else {
        throw new Error('No session token returned');
      }
    } catch {
      setError('session_failed');
      setStatus('error');
      track('voice_call_error', { errorType: 'session_failed' });
    }
  }, []);

  const endCall = useCallback(async () => {
    try {
      await conversationRef.current?.endSession();
    } catch {
      setStatus('ended');
      setTimeout(() => setStatus('idle'), 3000);
    }
  }, []);

  const dismissHUD = useCallback(() => {
    setStatus('idle');
    setError(null);
  }, []);

  // Escape key handler — only active while a call is live
  useEffect(() => {
    if (status !== 'connected' && status !== 'connecting') return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') endCall(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [status, endCall]);

  return (
    <>
      <ConversationManager
        onConnect={onConnect}
        onDisconnect={onDisconnect}
        onMessage={onMessage}
        onError={onError}
        onIsSpeakingChange={onIsSpeakingChange}
        conversationRef={conversationRef}
      />
      <VoiceCallContext.Provider
        value={{ status, error, transcript, isSpeaking, startCall, endCall, dismissHUD }}
      >
        {children}
      </VoiceCallContext.Provider>
    </>
  );
};

export const useVoiceCall = (): VoiceCallContextValue => {
  const ctx = useContext(VoiceCallContext);
  if (!ctx) throw new Error('useVoiceCall must be used within VoiceCallProvider');
  return ctx;
};
